import type { CashFlowStepStatus, CashFlowTimelineStep } from '@kioskgaming/ui';
import { formatInTimezone, getClientTimezone } from '@kioskgaming/ui';
import type { Transaction } from '../types';
import { resolveLifecycleFlowKind } from './transactionLifecycleFlow';

function formatCredits(amount: number, isOut: boolean): string {
  const formatted = Math.abs(amount).toLocaleString('en-US');
  return isOut ? `-${formatted} credits` : `+${formatted} credits`;
}

function formatCreditsBalance(amount: number | string | undefined | null): string | undefined {
  if (amount == null) return undefined;
  const n = Number(amount);
  if (!Number.isFinite(n)) return undefined;
  return `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(n)} credits`;
}

function formatTimestamp(iso?: string | null, timezone = getClientTimezone()): string | undefined {
  if (!iso) return undefined;
  const result = formatInTimezone(iso, timezone);
  return result === '—' ? undefined : result;
}

function normalizeStatus(status: string | null | undefined): string {
  return String(status || '').trim().toLowerCase();
}

function mapCashFlowStatus(status: string | null | undefined): CashFlowStepStatus | undefined {
  const s = normalizeStatus(status);
  if (s === 'completed' || s === 'approved' || s === 'success') return 'success';
  if (s === 'failed' || s === 'rejected' || s === 'cancelled') return 'failed';
  if (s === 'pending' || s === 'processing') return 'pending';
  return undefined;
}

function gameProviderLabel(transaction: Transaction): string {
  const provider =
    transaction.gameWithdrawalDetail?.gameProvider ||
    transaction.gameProvider ||
    null;
  return provider?.name || provider?.code || 'Game';
}

function withdrawalAmount(transaction: Transaction): number {
  const gwt = transaction.gameWalletTransaction;
  const detail = transaction.gameWithdrawalDetail;
  const raw = gwt?.amount ?? detail?.amount ?? transaction.amount ?? 0;
  return Math.abs(Number(raw || 0));
}

export function shouldBuildGameWithdrawalCashFlow(transaction: Transaction): boolean {
  if (transaction.type !== 'withdrawal') return false;
  if (resolveLifecycleFlowKind(transaction) !== 'withdrawal_game_to_wallet') return false;
  if (!transaction.gameWithdrawalDetail && !transaction.gameWalletTransaction && !transaction.gameProvider) {
    return false;
  }
  return withdrawalAmount(transaction) > 0;
}

/** Game → master withdrawal — synthesize game + master wallet legs from detail payload. */
export function buildGameWithdrawalCashFlowSteps(
  transaction: Transaction,
  timezone = getClientTimezone(),
): CashFlowTimelineStep[] {
  const detail = transaction.gameWithdrawalDetail;
  const gwt = transaction.gameWalletTransaction;
  const amount = withdrawalAmount(transaction);
  const gameLabel = gameProviderLabel(transaction);
  const gameCode =
    detail?.gameProvider?.code || transaction.gameProvider?.code || undefined;

  const status = gwt?.status || detail?.status || transaction.status;
  const isCompleted = normalizeStatus(status) === 'completed';
  const isFailed = ['failed', 'rejected', 'cancelled'].includes(normalizeStatus(status));

  const timestamp = formatTimestamp(
    gwt?.updatedAt || gwt?.createdAt || detail?.createdAt || transaction.updated_at || transaction.created_at,
    timezone,
  );

  const gameOutStep: CashFlowTimelineStep = {
    id: 'game-withdrawal-out',
    variant: 'source',
    walletName: 'Player game wallet',
    subtitle: gameCode ? `${gameLabel} · ${gameCode}` : gameLabel,
    direction: 'out',
    amount: formatCredits(amount, true),
    status: mapCashFlowStatus(status) || 'pending',
    actionLabel: 'Withdraw credits from game wallet',
    timestamp,
    balance:
      (gwt?.gameBalanceBefore ?? detail?.gameBalanceBefore) != null &&
      (gwt?.gameBalanceAfter ?? detail?.gameBalanceAfter) != null
        ? {
            before: formatCreditsBalance(gwt?.gameBalanceBefore ?? detail?.gameBalanceBefore),
            after: formatCreditsBalance(gwt?.gameBalanceAfter ?? detail?.gameBalanceAfter),
          }
        : undefined,
    connectorAfter: { label: 'Transfer to master wallet' },
  };

  const masterInStep: CashFlowTimelineStep = {
    id: 'game-withdrawal-in',
    walletName: 'Player master wallet',
    direction: 'in',
    amount: formatCredits(amount, false),
    status: isCompleted ? 'success' : isFailed ? 'failed' : 'pending',
    actionLabel: isFailed
      ? 'Transfer failed — master wallet not credited'
      : 'Credits credited to master wallet',
    timestamp,
    hideStatusInDetails: true,
    balance:
      (gwt?.walletBalanceBefore ?? detail?.walletBalanceBefore) != null &&
      (gwt?.walletBalanceAfter ?? detail?.walletBalanceAfter) != null
        ? {
            before: formatCreditsBalance(gwt?.walletBalanceBefore ?? detail?.walletBalanceBefore),
            after: formatCreditsBalance(gwt?.walletBalanceAfter ?? detail?.walletBalanceAfter),
          }
        : undefined,
  };

  return [gameOutStep, masterInStep];
}
